/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { eosApiService } from '../../../services/api/api.eos'
import TableMenu from './TableMenu'
import type { EosSiteRow, EosPaginatedResponse, EosTableQueryParams } from './types'
import Loader from '../../../components/shared/Loader'

// ─── Constants ───────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 10
const PERSISTENCE_KEY = 'EOS_DASHBOARD_SITES_TABLE'

// ─── Component ───────────────────────────────────────────────────────────

const SitesTable: React.FC = () => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<EosSiteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('STUDY_NAME')
  const [sortDesc, setSortDesc] = useState(true)
  const queryRef = useRef<EosTableQueryParams>({
    pageStart: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'STUDY_NAME',
    sortDesc: true
  })

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const queryParams = {
        ...queryRef.current,
        sortBy,
        sortDesc
      }
      const resp = await eosApiService.getUserSites(queryParams)
      const data = resp as EosPaginatedResponse<EosSiteRow>
      setEntries(data.elements || [])
    } catch {
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [sortBy, sortDesc])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSort = useCallback(
    (column: string) => {
      if (sortBy === column) {
        setSortDesc(!sortDesc)
      } else {
        setSortBy(column)
        setSortDesc(true)
      }
    },
    [sortBy, sortDesc]
  )

  const renderSortIcon = (column: string) => {
    if (sortBy !== column) return null
    return <span className={`sort-icon ${sortDesc ? 'sort-desc' : 'sort-asc'}`}>{sortDesc ? ' ▼' : ' ▲'}</span>
  }

  if ((!entries || entries.length === 0) && !loading) return null

  return (
    <div>
      <div>
        <h2>{t('EOS_DASHBOARD_SITES_TABLE_TITLE')}</h2>
      </div>
      <Loader blocking={loading}>
        <table className="table table-striped table-hover" data-persistence-key={PERSISTENCE_KEY}>
          <thead>
            <tr>
              <th
                className="sortable"
                onClick={() => handleSort('STUDY_NAME')}
                role="columnheader"
                aria-sort={sortBy === 'STUDY_NAME' ? (sortDesc ? 'descending' : 'ascending') : 'none'}
              >
                {t('TABLE_TH_STUDY_NAME')}
                {renderSortIcon('STUDY_NAME')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('SITE_NAME')}
                role="columnheader"
                aria-sort={sortBy === 'SITE_NAME' ? (sortDesc ? 'descending' : 'ascending') : 'none'}
              >
                {t('TABLE_TH_SITE_NAME')}
                {renderSortIcon('SITE_NAME')}
              </th>
              <th id="fd4ea078-7d1c-4550-be84-14ead0e2f602">{t('TASKS_TABLE_HEADER_ACTIONS')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((row) => (
              <tr key={row.siteFolderId}>
                <td>{row.studyName}</td>
                <td>{row.siteName}</td>
                <td className="actions">
                  <TableMenu item={row} itemKey={row.siteFolderId} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Loader>
    </div>
  )
}

export default SitesTable
